import { z } from 'zod'
import { objectToCamel, objectToSnake } from 'ts-case-convert'
import * as FabricSchemas from './fabric' // avoiding circular dependency

//* ---------------- BASE ---------------- *//
const baseCollectionSchema = z.object({
  id: z.union([z.number(), z.literal('favorites')]),
  name: z.string(),
  title: z.string(),
  datecode: z.string(),
  image: z.string(),
  digital_pdf_url: z.string().nullable(),
  brand: z.string().nullable(),
  year: z.number().nullable(),
})

const baseCollectionExtendedSchema = baseCollectionSchema.extend({
  description: z.string().nullable(),
  fabrics: z.lazy(() => z.array(FabricSchemas.fabricSchema)),
  life_cycle: z.string().nullable().optional(),
  origin: z.string().nullable().optional(),
})

//* ---------------- SCHEMAS ---------------- *//
export const collectionSchema = baseCollectionSchema.transform(objectToCamel)

export const collectionExtendedSchema = baseCollectionExtendedSchema.transform(objectToCamel)

export const collectionAdminExtendedSchema = baseCollectionExtendedSchema
  .extend({
    id: z.number(),
    key: z.string().nullable(),
    brand_id: z.number().nullable(),
    cross_coded: z.boolean(),
    sl_capable: z.boolean(),
    can_reorder: z.boolean(),
    is_demo: z.boolean(),
    is_live: z.boolean(),
    min_yards: z.number().nullable(),
    max_yards: z.number().nullable(),
    ship_method: z.string().nullable(),
    garment_type: z.number(),
    trim_garment_type: z.number(),
    collection_type: z.enum(['shirting', 'clothing']),
    collection_usage: z.enum(['shell', 'trim']),
  })
  .transform(objectToCamel)

export const collectionMetricsSchema = baseCollectionSchema
  .extend({
    stock_percent: z.number().nullable(),
    three_month_avg_count: z.number().nullable(),
    nine_month_avg_count: z.number().nullable(),
    three_month_consumption: z.number().nullable(),
    inventory: z.number().nullable(),
  })
  .transform(objectToCamel)

export const collectionBrandSchema = z
  .object({
    id: z.number(),
    brand: z.string(),
    is_active: z.boolean(),
    created_at: z.coerce.date(),
    updated_at: z.coerce.date(),
  })
  .transform(objectToCamel)

export const collectionFiltersSchema = z.object({
  brands: z.array(z.string()),
  years: z.array(z.coerce.string()),
})

//* ---------------- DATA SCHEMAS ---------------- *//
const baseCollectionDataSchema = z.object({
  name: z.string().nonempty(),
  key: z.string().optional(),
  description: z.string().optional(),
  brandId: z.coerce
    .number()
    .nullable()
    .optional()
    .transform(val => (val === 0 ? undefined : val)),
  year: z.coerce.number().nullable().optional(),
  lifeCycle: z.enum(['1 year', '2 years', '3+ years', 'until sold out']).optional(),
  origin: z.string().optional(),
  shipMethod: z.enum(['ocean', 'air']).nullable().optional(),
  collectionType: z.enum(['clothing', 'shirting']),
  collectionUsage: z.enum(['trim', 'shell']),
  digitalPdfUrl: z.string().nullable().optional(),
  crossCoded: z.coerce.boolean().optional(),
  slCapable: z.coerce.boolean().optional(),
  canReorder: z.coerce.boolean().optional(),
  isDemo: z.coerce.boolean().optional(),
  isLive: z.coerce.boolean().optional(),
  minYards: z.coerce
    .number()
    .optional()
    .transform(val => (val === 0 ? undefined : val)),
  maxYards: z.coerce
    .number()
    .optional()
    .transform(val => (val === 0 ? undefined : val)),
})

export const createCollectionDataSchema = baseCollectionDataSchema
  .extend({
    year: z.coerce.number(),
    lifeCycle: z.enum(['1 year', '2 years', '3+ years', 'until sold out'], {
      errorMap: iss => ({ message: iss.code === 'invalid_enum_value' ? 'Invalid Entry' : (iss.message ?? 'Invalid') }),
    }),
    origin: z.string(),
    shipMethod: z.enum(['ocean', 'air'], {
      errorMap: iss => ({ message: iss.code === 'invalid_enum_value' ? 'Invalid Entry' : (iss.message ?? 'Invalid') }),
    }),
  })
  .transform(objectToSnake)

export const updateCollectionDataSchema = baseCollectionDataSchema.transform(objectToSnake)

//* ---------------- TYPES ---------------- *//
declare global {
  namespace TrinityAPI {
    type CollectionType = z.infer<typeof collectionSchema>
    type CollectionExtendedType = z.infer<typeof collectionExtendedSchema>
    type CollectionExtendedAdminType = z.infer<typeof collectionAdminExtendedSchema>
    type CollectionBrandType = z.infer<typeof collectionBrandSchema>
    type CollectionFiltersType = z.infer<typeof collectionFiltersSchema>
    type CollectionMetricsType = z.infer<typeof collectionMetricsSchema>
  }
}
